<template>
  <div
    class="hld-result-container"
    v-loading="loading"
    element-loading-text="生成报告中..."
  >
    <!-- 顶部背景与导航 -->
    <div class="bg-header">
      <div class="nav-bar">
        <i class="el-icon-arrow-left back-icon" @click="goBack"></i>
        <span class="title"></span>
        <div></div>
      </div>

      <div class="header-titles">
        <h1>霍兰德职业兴趣测试报告</h1>
        <p class="date">生成于：{{ currentTime }}</p>
      </div>
    </div>

    <!-- 主体内容区 -->
    <div class="main-content" v-if="testResult && !error">
      <!-- 1. 霍兰德代码卡片 -->
      <div class="card code-card">
        <div class="code-header">
          <span class="label">您的霍兰德代码为：</span>
          <span class="primary-type"
            >{{ primaryTypeName }}：({{ primaryLetter }})</span
          >
        </div>

        <div class="code-display">
          <div class="main-code">{{ testResult.type }}</div>
          <div class="code-shadow"></div>
        </div>

        <div class="code-boxes">
          <div class="box" v-for="(letter, index) in codeLetters" :key="index">
            <div class="box-en">{{ letter }}</div>
            <div class="box-cn">{{ getTypeNameByLetter(letter) }}</div>
          </div>
        </div>
      </div>

      <!-- 2. 得分雷达图 -->
      <div class="section-title">您的得分图：</div>
      <div class="card radar-card">
        <div id="radar-chart" class="chart-container"></div>
      </div>

      <!-- 3. 测评兴趣详情 -->
      <div class="section-title">您的测评兴趣：</div>
      <div class="card detail-card">
        <div
          class="card-item"
          v-for="(item, index) in testResult.detail.configVos"
          :key="index"
          :title="item.title"
          :content="item.content"
        >
          <h2 class="detail-main-title">
            {{ item.typeName }}:（{{ item.type }}）
          </h2>

          <!-- 共同特征 -->
          <div class="detail-block">
            <div class="block-header">
              <span class="block-title">共同特征</span>
              <span class="block-tag">{{ item.caption || "无" }}</span>
            </div>
            <p class="block-text">
              {{ item.surveyDataList[0].detail || "无" }}
            </p>
          </div>

          <!-- 典型特征 / 典型职业 -->
          <div class="detail-block">
            <div class="block-header">
              <span class="block-title">典型职业</span>
            </div>
            <p class="block-text">
              {{ item.surveyDataList[1].detail || "无" }}
            </p>
          </div>
        </div>

        <!-- 核心特质 -->
        <div class="detail-block" v-if="coreTraits.length > 0">
          <div class="block-header">
            <span class="block-title none-underline"
              >（{{ testResult.type }}）的核心特质</span
            >
          </div>
          <div class="tags-container">
            <span
              class="trait-tag"
              v-for="(trait, idx) in coreTraits"
              :key="idx"
              :style="{ backgroundColor: getTagColor(idx) }"
            >
              {{ trait }}
            </span>
          </div>
        </div>
      </div>

      <!-- 4. 适配职业 -->
      <div class="card career-card" v-if="adaptCareers">
        <div class="career-tag">适配职业</div>
        <p class="career-text">{{ adaptCareers }}</p>
      </div>

      <!-- 底部留白区域，防止被固定按钮遮挡 -->
      <div class="bottom-spacer"></div>
    </div>

    <!-- 错误提示 -->
    <div v-else-if="error && !loading" class="error-box">
      <p>获取测试结果失败，请重试</p>
    </div>

    <!-- 底部固定按钮 -->
    <div class="footer-buttons">
      <div class="btn btn-light" @click="shareLink">分享链接</div>
      <div class="btn btn-primary" @click="restartTest">再测一次</div>
    </div>
  </div>
</template>

<script>
import * as echarts from "echarts";
import { copyText } from "@/utils/clipboard";

export default {
  name: "HLDResult",
  data() {
    return {
      loading: true,
      error: false,
      testResult: null,
      currentTime: this.formatDate(new Date()),
      // 核心特质标签的颜色池 (对应设计图的蓝、橙、紫)
      tagColors: ["#38C4E8", "#F38A6B", "#7A91F8", "#42B983", "#F3BC2E"],
      // 完整的类型字典，用于映射代码
      typeDict: {
        R: "现实型",
        I: "研究型",
        A: "艺术型",
        S: "社会型",
        E: "企业型",
        C: "传统型",
      },
    };
  },
  computed: {
    // 提取代码中的字母数组，例如 "RES" -> ['R', 'E', 'S']
    codeLetters() {
      return this.testResult && this.testResult.type
        ? this.testResult.type.split("")
        : [];
    },
    // 主导字母 (第一个字母)
    primaryLetter() {
      return this.codeLetters.length > 0 ? this.codeLetters[0] : "";
    },
    // 主导类型全称
    primaryTypeName() {
      return this.getTypeNameByLetter(this.primaryLetter);
    },
    // 主导类型的简短描述 (从 configVos 中获取)
    primaryCaption() {
      if (
        !this.testResult ||
        !this.testResult.detail ||
        !this.testResult.detail.configVos
      )
        return "偏好动手实践、工具操作";
      const config = this.testResult.detail.configVos.find(
        (c) => c.type === this.primaryLetter
      );
      return config ? config.caption : "偏好动手实践、工具操作";
    },
    // 共同特征
    commonTraits() {
      return this.getDimensionText("共同特征");
    },
    // 典型特征/职业
    typicalTraits() {
      return (
        this.getDimensionText("典型特征") || this.getDimensionText("典型职业")
      );
    },
    // 核心特质 (切割为数组)
    coreTraits() {
      const text = this.getDimensionText("核心特质");
      if (!text) return [];
      return text.split(/\s*\+\s*|、/).filter((item) => item.trim() !== "");
    },
    // 适配职业 (设计图上是直接一段话，不需要切成列表)
    adaptCareers() {
      return this.getDimensionText("适配职业");
    },
  },
  mounted() {
    this.initData();
  },
  methods: {
    initData() {
      const openid =
        this.$route.query.openid ||
        localStorage.getItem("openId") ||
        localStorage.getItem("openid") ||
        "";
      const wjId =
        this.$route.query.wjId || localStorage.getItem("hldTestWjId") || "2";

      if (openid) {
        this.$get(
          `http://59.110.51.52:8010/system/web/getsurvey?openid=${encodeURIComponent(
            openid
          )}&wjId=${wjId}`
        )

          .then((res) => {
            if (res && res.code === 200) {
              this.testResult = res.data;
              this.$nextTick(() => {
                this.drawRadarChart();
              });
            } else {
              this.error = true;
            }
          })
          .catch((err) => {
            console.error("获取测试结果失败:", err);
            this.error = true;
          })
          .finally(() => {
            this.loading = false;
          });
      } else {
        this.error = true;
        this.loading = false;
      }
    },

    // 辅助：获取类型中文名称
    getTypeNameByLetter(letter) {
      return this.typeDict[letter] || "未知型";
    },

    // 辅助：从 surveyDataDtos 提取文本
    getDimensionText(dimName) {
      if (
        !this.testResult ||
        !this.testResult.detail ||
        !this.testResult.detail.surveyDataDtos
      )
        return "";
      const item = this.testResult.detail.surveyDataDtos.find(
        (d) => d.dimension === dimName
      );
      return item ? item.detail.trim() : "";
    },

    // 辅助：获取标签颜色
    getTagColor(index) {
      return this.tagColors[index % this.tagColors.length];
    },

    // 辅助：格式化时间
    formatDate(date) {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, "0");
      const d = String(date.getDate()).padStart(2, "0");
      const h = String(date.getHours()).padStart(2, "0");
      const min = String(date.getMinutes()).padStart(2, "0");
      const s = String(date.getSeconds()).padStart(2, "0");
      return `${y}-${m}-${d} ${h}:${min}:${s}`;
    },

    goBack() {
      this.$router.push("/hld-home");
    },
    restartTest() {
      this.$router.push("/hld-qa");
    },
    shareLink() {
      // 构建分享链接
      const shareUrl = `${window.location.origin}${window.location.pathname}${window.location.search}`;

      copyText(shareUrl)
        .then(() => {
          this.$message.success("链接已复制，去分享给好友吧！");
        })
        .catch((err) => {
          console.error("复制失败:", err);
          this.$message.error("复制失败，请手动复制链接");
        });
    },

    // 绘制雷达图 (完全按照设计图定制)
    drawRadarChart() {
      const chartDom = document.getElementById("radar-chart");
      if (!chartDom) return;

      let myChart = echarts.getInstanceByDom(chartDom);
      if (!myChart) {
        myChart = echarts.init(chartDom);
      }

      const scoreData =
        this.testResult && this.testResult.score ? this.testResult.score : [];
      const scoreMap = {};
      let maxScore = 10;

      scoreData.forEach((item) => {
        scoreMap[item.xxType] = item.num;
        if (item.num > maxScore) maxScore = item.num;
      });

      // 按照设计图顶点的固定顺序：艺术(A) -> 社会(S) -> 企业(E) -> 传统(C) -> 实际(R) -> 研究(I)
      const radarIndicators = [
        { type: "A", name: "艺术:(A)" },
        { type: "S", name: "社会:(S)" },
        { type: "E", name: "企业:(E)" },
        { type: "C", name: "传统:(C)" },
        { type: "R", name: "实际:(R)" },
        { type: "I", name: "研究:(I)" },
      ];

      const indicator = radarIndicators.map((item) => ({
        name: item.name,
        max: maxScore,
      }));
      const dataValues = radarIndicators.map(
        (item) => scoreMap[item.type] || 0
      );

      const option = {
        radar: {
          indicator: indicator,
          shape: "polygon",
          radius: "65%",
          splitNumber: 4,
          axisName: {
            formatter: (value, indicatorOpt) => {
              // 匹配类型字母以获取对应的分数
              const typeLetter = value.match(/\(([A-Z])\)/)[1];
              const score = scoreMap[typeLetter] || 0;
              return `{name|${value}}\n{score|${score}分}`;
            },
            rich: {
              name: {
                color: "#666",
                fontSize: 12,
                align: "center",
                lineHeight: 20,
              },
              score: {
                color: "#5C6BC0",
                fontSize: 14,
                fontWeight: "bold",
                align: "center",
              },
            },
          },
          splitArea: {
            areaStyle: { color: ["#fff", "#fff", "#fff", "#fff"] }, // 背景透明/纯白
          },
          axisLine: { lineStyle: { color: "#E0E4F5" } }, // 轴线颜色
          splitLine: { lineStyle: { color: "#E0E4F5" } }, // 网格线颜色
        },
        series: [
          {
            type: "radar",
            data: [
              {
                value: dataValues,
                name: "得分",
                areaStyle: { color: "rgba(122, 145, 248, 0.3)" }, // 内部填充色
                lineStyle: { color: "#7A91F8", width: 2 }, // 连线色
                itemStyle: { color: "#7A91F8", borderWidth: 2 }, // 拐点色
              },
            ],
          },
        ],
      };

      myChart.setOption(option);
      window.addEventListener("resize", () => {
        myChart.resize();
      });
    },
  },
};
</script>

<style scoped lang="scss">
/* 基础容器 */
.hld-result-container {
  max-width: 480px;
  margin: 0 auto;
  background-color: #f5f7ff;
  min-height: 100vh;
  font-family: "Helvetica Neue", Helvetica, "PingFang SC", "Hiragino Sans GB",
    "Microsoft YaHei", sans-serif;
  position: relative;
}

/* 顶部背景区 */
.bg-header {
  background: linear-gradient(180deg, #7a91f8 0%, #a4b4fb 100%);
  padding-bottom: 60px; /* 为卡片重叠留空间 */
}

/* 头部标题 */
.header-titles {
  text-align: center;
  color: #fff;
  padding: 20px 0;
}
.header-titles h1 {
  font-size: 20px;
  margin: 0 0 8px 0;
  font-weight: bold;
  letter-spacing: 1px;
}
.header-titles .date {
  font-size: 12px;
  opacity: 0.8;
  margin: 0;
}

/* 主体内容区 (向上偏移覆盖背景) */
.main-content {
  padding: 0 15px;
  margin-top: -40px;
  position: relative;
  z-index: 2;
}

/* 卡片通用样式 */
.card {
  background: #fff;
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: 0 4px 16px rgba(122, 145, 248, 0.08);
}

/* 1. 霍兰德代码卡片 */
.code-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}
.code-header .label {
  font-size: 14px;
  color: #333;
}
.code-header .primary-type {
  font-size: 14px;
  color: #5c6bc0;
  font-weight: bold;
}

.code-display {
  text-align: center;
  position: relative;
  margin: 10px 0 30px 0;
}
.main-code {
  font-size: 60px;
  font-weight: 900;
  color: #7a91f8;
  line-height: 1;
  letter-spacing: 4px;
  position: relative;
  z-index: 2;
}
.code-shadow {
  position: absolute;
  bottom: -10px;
  left: 50%;
  transform: translateX(-50%);
  width: 120px;
  height: 20px;
  background: radial-gradient(
    ellipse at center,
    rgba(122, 145, 248, 0.3) 0%,
    rgba(255, 255, 255, 0) 70%
  );
  z-index: 1;
}

.code-boxes {
  display: flex;
  justify-content: space-between;
  gap: 10px;
}
.box {
  flex: 1;
  border: 1px solid #eaeaea;
  border-radius: 8px;
  text-align: center;
}
.box-en {
  font-size: 16px;
  color: #666;
  padding: 10px 0;
  border-bottom: 1px solid #eaeaea;
  background-color: #fafafa;
}
.box-cn {
  font-size: 14px;
  color: #333;
  padding: 8px 0;
}

/* 分区标题 */
.section-title {
  font-size: 14px;
  color: #333;
  margin: 0 0 10px 5px;
}

/* 2. 雷达图卡片 */
.chart-container {
  width: 100%;
  height: 280px;
}

/* 3. 测评兴趣详情卡片 */
.detail-main-title {
  font-size: 22px;
  color: #5c6bc0;
  font-weight: bold;
  margin: 0 0 20px 0;
}

.detail-block {
  margin-bottom: 25px;
}
.block-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 12px;
}
.block-title {
  font-size: 16px;
  font-weight: bold;
  color: #333;
  position: relative;
  padding-bottom: 4px;
  &.none-underline {
    color: #6a80e6;
    &::after {
      display: none;
    }
  }
}
/* 标题底部的紫色下划线 */
.block-title::after {
  content: "";
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 3px;
  background-color: #d0d8ff;
  border-radius: 2px;
}
.block-tag {
  font-size: 12px;
  color: #5c6bc0;
  background-color: #e8eaf6;
  padding: 4px 10px;
  border-radius: 12px;
}

.block-text {
  font-size: 14px;
  color: #666;
  line-height: 1.8;
  text-align: justify;
  margin: 0;
}

.tags-container {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}
.trait-tag {
  color: #fff;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: bold;
}

/* 4. 适配职业卡片 */
.career-card {
  background: linear-gradient(to bottom, #fff, #fafafa);
}
.career-tag {
  display: inline-block;
  background-color: #fff3c4;
  color: #b58900;
  padding: 6px 15px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: bold;
  margin-bottom: 15px;
}
.career-text {
  font-size: 14px;
  color: #333;
  line-height: 1.8;
  margin: 0;
}

/* 底部留白与按钮 */
.bottom-spacer {
  height: 80px;
}

.footer-buttons {
  position: fixed;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  max-width: 480px;
  display: flex;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.05);
  z-index: 100;
}
.footer-buttons .btn {
  flex: 1;
  text-align: center;
  padding: 18px 0;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
}
.btn-light {
  background-color: #fff;
  color: #5c6bc0;
}
.btn-primary {
  background-color: #6a84f5;
  color: #fff;
}

/* 错误提示 */
.error-box {
  text-align: center;
  padding: 50px 0;
  color: #999;
}
</style>
